import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, Combine, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/error';
import {
  createSenderAlias,
  deleteSenderAlias,
  dismissSenderAliasSuggestion,
  fetchSenderAliases,
  fetchSenderAliasSuggestions,
  type SenderAlias,
  type SenderAliasSuggestion,
} from '@/api/senderAliases';

const suggestionKey = (s: SenderAliasSuggestion) => `${s.localPrefix}@${s.domain}`;
const patternLabel = (localPrefix: string, domain: string) => `${localPrefix}*@${domain}`;

async function mergeSuggestion(suggestion: SenderAliasSuggestion) {
  const result = await createSenderAlias({
    domain: suggestion.domain,
    localPrefix: suggestion.localPrefix,
    canonicalAddress: suggestion.suggestedCanonicalAddress,
  });
  const mergedCount = result.merged.reduce((sum, entry) => sum + entry.mergedAddresses, 0);
  toast.success(
    `Merged ${mergedCount} address${mergedCount === 1 ? '' : 'es'} into ${suggestion.suggestedCanonicalAddress}`
  );
  // Open lists, pending cards and the reader update from the server's
  // contact.merged + per-message email.updated events (all clients).
}

function useSuggestionActions(onDone: (suggestion: SenderAliasSuggestion) => void) {
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const merge = useCallback(
    async (suggestion: SenderAliasSuggestion) => {
      setBusyKey(suggestionKey(suggestion));
      try {
        await mergeSuggestion(suggestion);
        onDone(suggestion);
      } catch (error) {
        toast.error(`Couldn't merge senders: ${getErrorMessage(error)}`);
      } finally {
        setBusyKey(null);
      }
    },
    [onDone]
  );

  const dismiss = useCallback(
    async (suggestion: SenderAliasSuggestion) => {
      setBusyKey(suggestionKey(suggestion));
      try {
        await dismissSenderAliasSuggestion({ domain: suggestion.domain, localPrefix: suggestion.localPrefix });
        onDone(suggestion);
      } catch (error) {
        toast.error(`Couldn't dismiss suggestion: ${getErrorMessage(error)}`);
      } finally {
        setBusyKey(null);
      }
    },
    [onDone]
  );

  return { busyKey, merge, dismiss };
}

function SuggestionRow({
  suggestion,
  busy,
  onMerge,
  onDismiss,
  compact = false,
}: {
  suggestion: SenderAliasSuggestion;
  busy: boolean;
  onMerge: () => void;
  onDismiss: () => void;
  compact?: boolean;
}) {
  return (
    <div className={cn('flex items-center gap-3 rounded-xl border bg-card px-3 py-2', compact && 'py-1.5')}>
      <Combine className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">
          <span className="font-medium">{suggestion.displayName ?? suggestion.domain}</span>
          <span className="text-muted-foreground">
            {' '}uses {suggestion.addressCount} different addresses
            {suggestion.pendingCount > 0 ? ` (${suggestion.pendingCount} pending)` : ''}
          </span>
        </div>
        <div className="truncate font-mono text-xs text-muted-foreground">
          {patternLabel(suggestion.localPrefix, suggestion.domain)} → {suggestion.suggestedCanonicalAddress}
        </div>
      </div>
      <Button size="sm" onClick={onMerge} disabled={busy}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Merge'}
      </Button>
      <Button size="icon" variant="ghost" onClick={onDismiss} disabled={busy} aria-label="Dismiss suggestion">
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * Pending-banner strip: offers a merge when a pending sender is one of a set
 * of tokenized addresses (e.g. no-reply-<token>@mail.anthropic.com).
 * `pendingKey` changes whenever the pending list does, to re-check.
 */
export function PendingAliasSuggestions({ pendingKey, className }: { pendingKey: string; className?: string }) {
  const [suggestions, setSuggestions] = useState<SenderAliasSuggestion[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchSenderAliasSuggestions({ pendingOnly: true, signal: controller.signal })
      .then(setSuggestions)
      .catch((error) => {
        if (!controller.signal.aborted) {
          console.error('[PendingAliasSuggestions] failed to load suggestions', error);
        }
      });
    return () => controller.abort();
  }, [pendingKey]);

  const removeSuggestion = useCallback((done: SenderAliasSuggestion) => {
    setSuggestions((prev) => prev.filter((s) => suggestionKey(s) !== suggestionKey(done)));
  }, []);
  const { busyKey, merge, dismiss } = useSuggestionActions(removeSuggestion);

  if (!suggestions.length) {
    return null;
  }

  return (
    <div className={cn('space-y-2 px-4 pt-3', className)}>
      {suggestions.map((suggestion) => (
        <SuggestionRow
          key={suggestionKey(suggestion)}
          suggestion={suggestion}
          busy={busyKey === suggestionKey(suggestion)}
          onMerge={() => void merge(suggestion)}
          onDismiss={() => void dismiss(suggestion)}
          compact
        />
      ))}
    </div>
  );
}

/** Settings → Contacts: merged-sender rules plus all current suggestions. */
export function SenderAliasesSection() {
  const [open, setOpen] = useState(false);
  const [aliases, setAliases] = useState<SenderAlias[] | null>(null);
  const [suggestions, setSuggestions] = useState<SenderAliasSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const [nextAliases, nextSuggestions] = await Promise.all([
        fetchSenderAliases(signal),
        fetchSenderAliasSuggestions({ signal }),
      ]);
      setAliases(nextAliases);
      setSuggestions(nextSuggestions);
      setError(null);
    } catch (err) {
      if (!signal?.aborted) {
        setError(getErrorMessage(err));
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => {
    void load();
  }, [load]);
  const { busyKey, merge, dismiss } = useSuggestionActions(reload);

  const handleRemove = useCallback(
    async (alias: SenderAlias) => {
      setRemovingId(alias.id);
      try {
        await deleteSenderAlias(alias.id);
        setAliases((prev) => prev?.filter((a) => a.id !== alias.id) ?? null);
      } catch (err) {
        toast.error(`Couldn't remove rule: ${getErrorMessage(err)}`);
      } finally {
        setRemovingId(null);
      }
    },
    []
  );

  const suggestionCount = suggestions?.length ?? 0;
  const aliasCount = aliases?.length ?? 0;

  return (
    <div className="border-b border-border/40 px-4 py-2">
      <button
        type="button"
        className="flex w-full items-center gap-2 py-1 text-left text-sm font-medium"
        onClick={() => setOpen((prev) => !prev)}
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Merged senders
        {aliasCount > 0 && <Badge variant="secondary">{aliasCount}</Badge>}
        {suggestionCount > 0 && <Badge>{suggestionCount} suggested</Badge>}
      </button>
      {open && (
        <div className="space-y-3 pb-2 pt-2">
          <p className="text-xs text-muted-foreground">
            Some senders use a new address for every message. Merging treats every matching address
            as one sender for pending, approve/block and grouping. Removing a rule only affects new
            mail; senders already merged stay merged.
          </p>
          {error && <p className="text-xs text-destructive">{error}</p>}
          {suggestions?.map((suggestion) => (
            <SuggestionRow
              key={suggestionKey(suggestion)}
              suggestion={suggestion}
              busy={busyKey === suggestionKey(suggestion)}
              onMerge={() => void merge(suggestion)}
              onDismiss={() => void dismiss(suggestion)}
            />
          ))}
          {aliases?.map((alias) => (
            <div key={alias.id} className="flex items-center gap-3 rounded-xl border px-3 py-2">
              <div className="min-w-0 flex-1 truncate font-mono text-xs">
                {patternLabel(alias.localPrefix, alias.domain)} → {alias.canonicalAddress}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void handleRemove(alias)}
                disabled={removingId === alias.id}
              >
                {removingId === alias.id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
              </Button>
            </div>
          ))}
          {aliases && suggestions && aliasCount === 0 && suggestionCount === 0 && (
            <p className="text-xs text-muted-foreground">No merged senders or suggestions.</p>
          )}
        </div>
      )}
    </div>
  );
}
